import { Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { Subject, takeUntil } from 'rxjs';

type NavItem = {
    name: string;
    href?: string;
    icon?: string;
    children?: NavItem[];
    collapsible?: boolean;
};

type FlatNavItem = {
    name: string;
    mobileLabel: string;
    href: string;
    icon: string;
};

@Component({
    selector: 'app-mobile-bottom-nav',
    standalone: true,
    imports: [CommonModule, RouterLink, LucideIconsModule],
    templateUrl: './mobile-bottom-nav.component.html',
    styleUrls: ['./mobile-bottom-nav.component.scss'],
})
export class MobileBottomNavComponent implements OnChanges, OnDestroy {
    @Input() navigation: NavItem[] = [];

    flatNavigation: FlatNavItem[] = [];
    moreItems: FlatNavItem[] = [];
    moreOpen = false;

    // Active state is computed from the router URL (same rules as
    // SidebarComponent): RouterLinkActive cannot express "Offers active on
    // /developer/offers/* except /developer/offers/approvals".
    private readonly destroy$ = new Subject<void>();
    private readonly router = inject(Router);
    currentUrl = this.router.url || '';

    constructor() {
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.currentUrl = event.urlAfterRedirects || event.url || '';
                this.moreOpen = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(): void {
        const primary: FlatNavItem[] = [];
        const more: FlatNavItem[] = [];
        const seen = new Set<string>();
        for (const item of this.navigation || []) {
            if (item?.collapsible && item.children?.length) {
                for (const child of item.children) {
                    const href = String(child?.href || '').trim();
                    if (!href || seen.has(href)) continue;
                    seen.add(href);
                    more.push(this.toFlat(child));
                }
                continue;
            }
            if (item?.children?.length) {
                for (const child of item.children) {
                    const href = String(child?.href || '').trim();
                    if (!href || seen.has(href)) continue;
                    seen.add(href);
                    primary.push(this.toFlat(child));
                }
                continue;
            }
            const href = String(item?.href || '').trim();
            if (!href || seen.has(href)) continue;
            seen.add(href);
            primary.push(this.toFlat(item));
        }
        this.flatNavigation = primary;
        this.moreItems = more;
        this.moreOpen = false;
    }

    get hasMore(): boolean {
        return this.moreItems.length > 0;
    }

    get totalSlots(): number {
        return this.flatNavigation.length + (this.hasMore ? 1 : 0);
    }

    get useDistributedLayout(): boolean {
        return this.totalSlots > 0 && this.totalSlots <= 4;
    }

    get distributedTemplateColumns(): string {
        const count = Math.max(1, this.totalSlots);
        return `repeat(${count}, minmax(0, 1fr))`;
    }

    toggleMore(): void {
        this.moreOpen = !this.moreOpen;
    }

    closeMore(): void {
        this.moreOpen = false;
    }

    private matchesPath(url: string, path: string): boolean {
        return url === path || url.startsWith(path + '/');
    }

    private allNavHrefs(): string[] {
        const out: string[] = [];
        for (const item of this.flatNavigation || []) if (item?.href) out.push(item.href);
        for (const item of this.moreItems || []) if (item?.href) out.push(item.href);
        return out;
    }

    /**
     * Longest matching nav href wins (same rule as SidebarComponent): a parent
     * item must not stay lit when the URL is inside a sibling child route
     * (/developer/offers vs /developer/offers/approvals lit both chips).
     */
    isNavActive(href?: string): boolean {
        const path = String(href || '').trim();
        if (!path) return false;
        const url = String(this.currentUrl || '').split('?')[0].split('#')[0];
        if (!url || !this.matchesPath(url, path)) return false;
        return !this.allNavHrefs().some(
            (other) => other !== path && other.length > path.length && this.matchesPath(url, other)
        );
    }

    /** Active chip classes, or '' when the href is not the current page. */
    navActiveClasses(href?: string): string {
        return this.isNavActive(href)
            ? 'bg-[color:var(--secondary)] text-[color:var(--primary)] border-[color:var(--ring)] shadow-sm'
            : '';
    }

    private toFlat(item: NavItem | undefined): FlatNavItem {
        return {
            name: String(item?.name || 'Page'),
            mobileLabel: this.mobileLabel(String(item?.name || 'Page')),
            href: String(item?.href || ''),
            icon: String(item?.icon || 'layout-dashboard')
        };
    }

    private mobileLabel(name: string): string {
        const raw = String(name || '').trim();
        const mapped = new Map<string, string>([
            ['Manage Properties', 'Properties'],
            ['Manage Leads', 'Leads'],
            ['Manage Partners', 'Partners'],
            ['Manage Team', 'Team'],
            ['Upload Property', 'Upload\nProperty'],
            ['Add New Project', 'New\nProject'],
            ['Property Approvals', 'Property\nApprovals'],
            ['Deleted Properties', 'Deleted\nProperties'],
            ['Manage Projects', 'Projects'],
            ['Project Approvals', 'Project\nApprovals'],
            ['Verify Projects', 'Verify\nProjects'],
            ['Deleted Projects', 'Deleted\nProjects'],
            ['Audit Logs', 'Audit\nLogs'],
            ['Signup Links', 'Signup\nLinks'],
            ['System Leads', 'System\nLeads'],
            ['Identity Reviews', 'Identity\nReviews'],
            ['Organizations', 'Orgs'],
            ['Prod To Dev Sync', 'Prod-Dev\nSync'],
            ['System Features', 'System\nFeatures']
        ]);
        return mapped.get(raw) || raw;
    }
}
